import { getDb, getDbForCurrentUser } from "./rxdb/database";
import {
  DIARY_DAYS_COLLECTION,
  DIARY_DAY_OUTBOX_COLLECTION,
  SERVER_MODIFIER_ID,
} from "@macro-tracker/models";
import type {
  DiaryDayGoalSnapshot,
  DiaryDayEntrySourceType,
  DiaryDayEntry,
  DiaryMealPlanKind,
  DiaryMealTarget,
  DiaryMealExercise,
  DiaryMealPlanned,
  DiaryMealCustom,
  DiaryMealPeriodization,
  DiaryMealPlanPlannerResult,
  DiaryWorkoutActuals,
  DiaryWorkoutSupportSnack,
  DiaryWorkoutSupportSnackSuppression,
  DiaryMeal,
  DiaryDayImportedTotals,
  DiaryDayPlanGenerationStatus,
  DiaryDayPlanGeneration,
  DiaryDayBody,
  DiaryDayDocType,
} from "@macro-tracker/models";
import type { Subscription } from "rxjs";
import { getDeviceModifierId } from "./deviceIdentity";
import { backfillDiaryDayBody } from "./diaryDayBackfill";
import { markDiaryDayDirty } from "./rxdb/diaryOutbox";
import { scheduleDiaryReplication } from "./rxdb/replication";

// Re-export canonical types so existing importers keep working without changes.
export type {
  DiaryDayGoalSnapshot,
  DiaryDayEntrySourceType,
  DiaryDayEntry,
  DiaryMealPlanKind,
  DiaryMealTarget,
  DiaryMealExercise,
  DiaryMealPlanned,
  DiaryMealCustom,
  DiaryMealPeriodization,
  DiaryMealPlanPlannerResult,
  DiaryWorkoutActuals,
  DiaryWorkoutSupportSnack,
  DiaryWorkoutSupportSnackSuppression,
  DiaryMeal,
  DiaryDayImportedTotals,
  DiaryDayPlanGenerationStatus,
  DiaryDayPlanGeneration,
  DiaryDayBody,
};
// Re-export the resolver helpers (values) so consumers can read effective meal
// values without importing from the models package directly.
export {
  resolveMealTitle,
  resolveMealPlannedAt,
  resolveMealTarget,
  resolveMealExercise,
  isMealCustomized,
} from "@macro-tracker/models";

/** @deprecated Inline `'carbs' | 'fat'` or use the flexMacro field directly. */
export type DiaryGoalFlexMacro = 'carbs' | 'fat';

/** Frontend utility: key names shared across macro target objects. */
export type DiaryMealPlanMacroKey = 'calories' | 'protein' | 'carbs' | 'fat';

/**
 * Canonical diary-day document shape. Aliased from `DiaryDayDocType` so
 * frontend and DB types cannot drift: any change to the persisted schema
 * propagates here automatically via `packages/models`.
 */
export type DiaryDay = DiaryDayDocType;

export const diaryDayId = (userId: string, date: string): string =>
  `${userId}:${date}`;

export const emptyDiaryDayPlanGeneration = (): DiaryDayPlanGeneration => ({
  generatedForDay: false,
  generatedAt: null,
  lastInputHash: null,
  status: "idle",
  errorMessage: null,
  plannerDiagnostics: null,
});

export const emptyDiaryDayBody = (): DiaryDayBody => ({
  goalSnapshot: null,
  meals: [],
  importedTotals: null,
  planGeneration: emptyDiaryDayPlanGeneration(),
});

export const createEmptyDiaryDay = (
  userId: string,
  date: string,
): DiaryDay => {
  const now = new Date().toISOString();
  return {
    id: diaryDayId(userId, date),
    userId,
    date,
    body: emptyDiaryDayBody(),
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
    syncRevision: 0,
    modifiedBy: SERVER_MODIFIER_ID,
  };
};

const toPersistableDiaryDayBody = (body: DiaryDayBody): DiaryDayBody =>
  JSON.parse(JSON.stringify(body)) as DiaryDayBody;

// ---------------------------------------------------------------------------
// RxDB helpers
// ---------------------------------------------------------------------------

/** Convert an RxDB document (or plain doc data) into the frontend DiaryDay shape. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const docToDay = (doc: any): DiaryDay => ({
  id: doc.id as string,
  userId: doc.userId as string,
  date: doc.date as string,
  body: backfillDiaryDayBody(doc.body as DiaryDayBody),
  createdAt: doc.createdAt as string,
  updatedAt: doc.updatedAt as string,
  deletedAt: (doc.deletedAt ?? null) as string | null,
  syncRevision: Number(doc.syncRevision ?? 0),
  modifiedBy: String(doc.modifiedBy ?? SERVER_MODIFIER_ID),
});

const getDiaryDaysCollection = async (userId: string) => {
  const db = await getDb(userId);
  return db[DIARY_DAYS_COLLECTION];
};

/**
 * Subscribe to the RxDB document for `userId:date`. The callback is called
 * whenever the document changes (local mutation or remote replication write).
 * Returns an unsubscribe function.
 */
export const subscribeToDay = (
  userId: string,
  date: string,
  callback: (day: DiaryDay | null) => void,
): (() => void) => {
  let rxSub: Subscription | null = null;
  let cancelled = false;

  getDiaryDaysCollection(userId).then((col) => {
    if (cancelled) return;
    rxSub = col
      .findOne({ selector: { userId, date } })
      .$.subscribe((doc) => {
        if (doc && !doc.deletedAt) {
          callback(docToDay(doc.toMutableJSON()));
        } else {
          callback(null);
        }
      });
  }).catch(console.error);

  return () => {
    cancelled = true;
    rxSub?.unsubscribe();
  };
};

export const getDiaryDay = async (
  userId: string,
  date: string,
): Promise<DiaryDay | null> => {
  const col = await getDiaryDaysCollection(userId);
  const doc = await col.findOne({ selector: { userId, date } }).exec();
  if (!doc || doc.deletedAt) return null;
  return docToDay(doc.toMutableJSON());
};

export const getAllDiaryDays = async (): Promise<DiaryDay[]> => {
  const db = await getDbForCurrentUser();
  const docs = await db[DIARY_DAYS_COLLECTION].find({
    selector: { deletedAt: null },
  }).exec();
  return docs.map((d) => docToDay(d.toMutableJSON()));
};

export const getDiaryDaysInRange = async (
  userId: string,
  from: string,
  to: string,
): Promise<DiaryDay[]> => {
  const col = await getDiaryDaysCollection(userId);
  const docs = await col
    .find({ selector: { userId, date: { $gte: from, $lte: to }, deletedAt: null } })
    .exec();
  return docs.map((d) => docToDay(d.toMutableJSON()));
};

export const upsertDiaryDay = async (day: DiaryDay): Promise<DiaryDay> => {
  const col = await getDiaryDaysCollection(day.userId);
  const modifiedBy = await getDeviceModifierId();
  await markDiaryDayDirty(day.userId, day.id);
  const now = new Date().toISOString();
  const body = toPersistableDiaryDayBody(backfillDiaryDayBody(day.body));
  const existing = await col.insertIfNotExists({
    ...day,
    body,
    syncRevision: 0,
    modifiedBy: SERVER_MODIFIER_ID,
  });
  const doc = await existing.incrementalModify((docData) => {
    docData.body = body as typeof docData.body;
    docData.updatedAt = now;
    docData.deletedAt = day.deletedAt ?? null;
    docData.syncRevision += 1;
    docData.modifiedBy = modifiedBy;
    return docData;
  });
  scheduleDiaryReplication(day.userId);
  return docToDay(doc.toMutableJSON());
};

/**
 * Load the day doc (creating an empty one if missing), pass it to `patcher` to
 * mutate body fields, bump updatedAt, and persist. This is the single mutation
 * entry point used by the diary store and chronometer/custom-food migrations.
 */
export const patchDiaryDay = async (
  userId: string,
  date: string,
  patcher: (body: DiaryDayBody, day: DiaryDay) => void | DiaryDayBody,
): Promise<DiaryDay> => {
  const col = await getDiaryDaysCollection(userId);
  const id = diaryDayId(userId, date);
  const modifiedBy = await getDeviceModifierId();
  await markDiaryDayDirty(userId, id);
  const base = createEmptyDiaryDay(userId, date);
  const existing = await col.insertIfNotExists(base);
  const updated = await existing.incrementalModify((docData) => {
    const day = docToDay(docData);
    const draft: DiaryDayBody = structuredClone(day.body);
    const result = patcher(draft, day);
    docData.body = toPersistableDiaryDayBody(
      (result ?? draft) as DiaryDayBody,
    ) as typeof docData.body;
    docData.updatedAt = new Date().toISOString();
    docData.deletedAt = null;
    docData.syncRevision += 1;
    docData.modifiedBy = modifiedBy;
    return docData;
  });
  scheduleDiaryReplication(userId);
  return docToDay(updated.toMutableJSON());
};

export const softDeleteDiaryDay = async (
  userId: string,
  date: string,
): Promise<boolean> => {
  const col = await getDiaryDaysCollection(userId);
  const doc = await col.findOne({ selector: { userId, date } }).exec();
  if (!doc) return false;
  const modifiedBy = await getDeviceModifierId();
  await markDiaryDayDirty(userId, doc.id);
  const now = new Date().toISOString();
  await doc.incrementalModify((docData) => {
    docData.deletedAt = now;
    docData.updatedAt = now;
    docData.syncRevision += 1;
    docData.modifiedBy = modifiedBy;
    return docData;
  });
  scheduleDiaryReplication(userId);
  return true;
};

/**
 * Iterate every non-deleted DiaryDay for the user and apply `patcher` to any
 * day whose meals reference `foodId`. Used by customFoodMigrations to rewrite
 * references across potentially many docs.
 */
export const forEachDayContainingFood = async (
  userId: string,
  foodId: string,
  patcher: (body: DiaryDayBody, day: DiaryDay) => void,
): Promise<number> => {
  const days = await getAllDiaryDays();
  let touched = 0;
  for (const day of days) {
    if (day.userId !== userId) continue;
    const hasFood = day.body.meals.some((meal) =>
      meal.entries.some((entry) => entry.source.foodId === foodId),
    );
    if (!hasFood) continue;
    await patchDiaryDay(userId, day.date, patcher);
    touched += 1;
  }
  return touched;
};

// ---------------------------------------------------------------------------
// Legacy sync stubs — no-ops now that RxDB handles sync internally.
// syncService.ts has been deleted; these stubs remain for any callers that
// may still reference the legacy API surface.
// ---------------------------------------------------------------------------

export const getDiaryDaysForSync = async (): Promise<DiaryDay[]> => [];
export const markDiaryDayForSync = async (
  _id: string,
  _operation: "create" | "update" | "delete",
): Promise<void> => {
  void _id;
  void _operation;
};
export const clearDiaryDaySyncStatus = async (_id: string): Promise<void> => {
  void _id;
};
export const saveDiaryDayFromSync = async (day: DiaryDay): Promise<DiaryDay> => day;
export const removeDiaryDayFromSync = async (_id: string): Promise<boolean> => {
  void _id;
  return true;
};

export const clearAllDiaryDays = async (userId?: string): Promise<void> => {
  const db = userId ? await getDb(userId) : await getDbForCurrentUser();
  const docs = await db[DIARY_DAYS_COLLECTION].find().exec();
  await Promise.all(docs.map((d) => d.remove()));
  const outboxDocs = await db[DIARY_DAY_OUTBOX_COLLECTION].find().exec();
  await Promise.all(outboxDocs.map((d) => d.incrementalPatch({ pending: false })));
};
